import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
import { useState, useCallback, useMemo, useRef } from 'react'
import { Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { Navigation } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { LocationMapPicker } from '@/components/hosting/LocationMapPicker'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { useLocation } from '@/lib/hooks/use-location'
import { geocodingService } from '@/lib/services/geocoding.service'
import { vehicleLocationSchema } from '@/lib/validations/vehicle.schema'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { captureError } from '@/lib/utils/observability'
import {
  DEFAULT_SV_LOCATION,
  isCoordinateInElSalvador,
  normalizeElSalvadorVehicleLocation,
  resolveKnownElSalvadorLocation,
} from '@/lib/utils/geo'
import { useEntrance } from '@/lib/hooks/use-entrance'

const DEFAULT_CENTER: [number, number] = [DEFAULT_SV_LOCATION.lng, DEFAULT_SV_LOCATION.lat]

export default function NewListingLocationScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel } = useListingStepNavigation('pricing')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const loc = useListingWizardStore((s) => s.location)
  const setLocation = useListingWizardStore((s) => s.setLocation)
  const pinnedByHost = useListingWizardStore((s) => s.homeLocationPinnedByHost)
  const setHomeLocationPinnedByHost = useListingWizardStore((s) => s.setHomeLocationPinnedByHost)
  const { location: userLocation } = useLocation()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isResolvingLocation, setIsResolvingLocation] = useState(false)
  const hasStoredSvLocation = isCoordinateInElSalvador(loc.homeLat, loc.homeLng)
  const knownAddressLocation = resolveKnownElSalvadorLocation(loc.homeAddress)
  const safeUserLocation = useMemo(
    () =>
      userLocation && isCoordinateInElSalvador(userLocation.lat, userLocation.lng)
        ? {
            lat: userLocation.lat,
            lng: userLocation.lng,
            name: userLocation.name ?? DEFAULT_SV_LOCATION.address,
          }
        : {
            lat: DEFAULT_SV_LOCATION.lat,
            lng: DEFAULT_SV_LOCATION.lng,
            name: DEFAULT_SV_LOCATION.address,
          },
    [userLocation],
  )

  const updateField = (key: string, value: unknown) => {
    setLocation({ ...loc, [key]: value })
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const center: [number, number] = [
    hasStoredSvLocation
      ? (loc.homeLng as number)
      : (knownAddressLocation?.lng ?? safeUserLocation.lng ?? DEFAULT_CENTER[0]),
    hasStoredSvLocation
      ? (loc.homeLat as number)
      : (knownAddressLocation?.lat ?? safeUserLocation.lat ?? DEFAULT_CENTER[1]),
  ]

  // Debounce map camera updates — fires once 300ms after pan stops
  const cameraTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleMapLocationPicked = useCallback(
    (lat: number, lng: number) => {
      if (!isCoordinateInElSalvador(lat, lng)) return
      if (cameraTimer.current) clearTimeout(cameraTimer.current)
      cameraTimer.current = setTimeout(() => {
        // Read CURRENT store state to avoid stale closure overwriting address/other fields
        const current = useListingWizardStore.getState().location
        setLocation({ ...current, homeLat: lat, homeLng: lng })
        // The picker only calls this for a real gesture, so this IS the host's answer.
        setHomeLocationPinnedByHost(true)
      }, 300)
    },
    [setLocation, setHomeLocationPinnedByHost],
  )

  const handleUseCurrentLocation = useCallback(() => {
    const current = useListingWizardStore.getState().location
    setLocation({
      ...current,
      homeLat: safeUserLocation.lat,
      homeLng: safeUserLocation.lng,
      homeAddress: safeUserLocation.name ?? current.homeAddress,
    })
    // Tapping "where I am" is as deliberate as dragging the pin.
    setHomeLocationPinnedByHost(true)
    void selection()
  }, [safeUserLocation, setLocation, setHomeLocationPinnedByHost])

  // The busy latch is released in a `finally`, never on individual branches.
  //
  // It used to be reset on exactly two paths — validation failure and success —
  // with no `finally` anywhere. Combined with the `if (isResolvingLocation)
  // return` guard at the top and a `geocodingService.forward` that was a raw
  // untimed `fetch`, a stalled connection latched this screen three ways at
  // once: the guard killed the handler permanently, the Button showed a spinner
  // forever, and the Button's own internal press lock latched too. The host
  // could neither continue nor retry — the wizard step was over.
  const handleNext = useCallback(async () => {
    if (isResolvingLocation) return

    setIsResolvingLocation(true)
    try {
      const address = loc.homeAddress ?? ''
      let normalizedLocation = normalizeElSalvadorVehicleLocation({
        homeAddress: address,
        homeLat: loc.homeLat ?? safeUserLocation.lat,
        homeLng: loc.homeLng ?? safeUserLocation.lng,
      })

      // THE GEOCODER FILLS A GAP; IT DOES NOT OVERRULE THE HOST.
      //
      // This used to run unconditionally and overwrite homeLat/homeLng with whatever the
      // geocoder returned for the typed text — including coordinates the host had just placed
      // by dragging the pin onto their own gate. Salvadoran addresses rarely carry a street
      // number, so the answer is a neighbourhood centroid: the host aimed at their house and
      // the guest was sent blocks away, with nothing on screen admitting the pin had moved.
      //
      // Now it runs only when the coordinates are still OURS — a device fix or the default —
      // which is exactly the case the geocoder was added for. `pinnedByHost` is set by a real
      // map gesture, by "use my location", and by loading a vehicle a host already saved.
      if (!pinnedByHost) {
        try {
          const geocodedResults = await geocodingService.forward(address)
          const geocoded = geocodedResults.find((result) =>
            isCoordinateInElSalvador(result.lat, result.lng),
          )
          if (geocoded) {
            normalizedLocation = {
              homeAddress: address.trim() || geocoded.address,
              homeLat: geocoded.lat,
              homeLng: geocoded.lng,
            }
          }
        } catch (err) {
          captureError(
            err,
            { screen: 'listing_location', action: 'forward-geocode' },
            { addressLength: address.trim().length },
          )
        }
      }

      const input = {
        homeAddress: normalizedLocation.homeAddress,
        homeLat: normalizedLocation.homeLat,
        homeLng: normalizedLocation.homeLng,
        countryId: loc.countryId ?? 'SV',
        cityId: loc.cityId,
        deliveryRadiusKm: loc.deliveryRadiusKm,
      }

      const result = vehicleLocationSchema.safeParse(input)
      if (!result.success) {
        const fieldErrors: Record<string, string> = {}
        for (const issue of result.error.issues) {
          const field = issue.path[0] as string
          fieldErrors[field] = t(issue.message)
        }
        setErrors(fieldErrors)
        void notification(NotificationType.Error)
        return
      }

      setLocation(result.data)
      continueEditing()
    } finally {
      setIsResolvingLocation(false)
    }
  }, [isResolvingLocation, loc, pinnedByHost, safeUserLocation, setLocation, continueEditing, t])

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          // flexGrow + a flex:1 map block below: the map is the whole point of
          // this step, so it takes every pixel between the address field and
          // the docked action bar instead of sitting in a 240pt letterbox with
          // a third of the screen empty under it.
          flexGrow: 1,
          padding: SPACING.lg,
          // The action bar is a sibling below this ScrollView, not an overlay
          // on top of it (same as the pricing step), so the old ~120pt of
          // trailing padding was dead background between the map and the
          // button rather than clearance for it.
          paddingBottom: SPACING.xl,
        }}
      >
        {/* Address */}
        <Animated.View collapsable={false} entering={enter(1)}>
          <TextInput
            label={t('listing.address')}
            value={loc.homeAddress ?? ''}
            onChangeText={(text) => updateField('homeAddress', text)}
            placeholder={t('listing.address_placeholder')}
            error={errors.homeAddress}
            testID="location.address-input"
          />
        </Animated.View>

        {/* Use current location — a chip, not bare coral text. The field above
            already carries FORM_FIELD_GAP underneath it, so the extra marginTop
            only widened the gap that left this row floating unattached. */}
        <Animated.View collapsable={false} entering={enter(2)}>
          <Pressable
            onPress={handleUseCurrentLocation}
            accessibilityRole="button"
            // Its own label. It used to reuse t('listing.address'), so TalkBack
            // announced this button identically to the address field above it —
            // a blind host heard "Dirección de recogida" twice and could not tell
            // that this one fills the address from GPS.
            accessibilityLabel={t('listing.use_current_location')}
            testID="location.use-current"
            style={({ pressed }) => ({
              alignSelf: 'flex-start',
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              minHeight: INTERACTION.minTouchTarget,
              paddingVertical: SPACING.sm,
              paddingHorizontal: SPACING.md,
              borderRadius: RADIUS.full,
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: colors.surface,
              opacity: pressed ? 0.72 : 1,
            })}
          >
            <Navigation size={16} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textAccent }}>
              {safeUserLocation.name ?? t('common.default_location')}
            </Text>
          </Pressable>
        </Animated.View>

        {/* Map — platform-specific: native uses Mapbox, web shows fallback */}
        <Animated.View
          collapsable={false}
          entering={enter(3)}
          style={{ flex: 1, marginTop: SPACING.md }}
        >
          <LocationMapPicker center={center} onLocationPicked={handleMapLocationPicked} />
        </Animated.View>
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          loading={isResolvingLocation}
          disabled={isResolvingLocation}
          testID="location.next-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
